'use client';

import { useEffect, useRef } from 'react';

/** Редактор из vanilla-jsoneditor: тип берётся у самой фабрики. */
type Editor = ReturnType<typeof import('vanilla-jsoneditor').createJSONEditor>;

/**
 * Панель редактора JSON поверх vanilla-jsoneditor.
 *
 * Библиотека собрана на Svelte и трогает DOM напрямую, поэтому React ею
 * не управляет: компонент лишь создаёт редактор в своём узле и передаёт
 * текст внутрь. Импорт динамический, потому что редактор весит сотни
 * килобайт и на сервере не работает вовсе.
 *
 * Наружу отдаётся именно текст, а не разобранное значение: документ может
 * быть сломан прямо во время набора, и терять набранное из-за этого нельзя.
 */
export function JsonEditorPane({
  value,
  onChange,
  readOnly = false,
  mode = 'text',
  label,
}: {
  value: string;
  onChange?: (value: string) => void;
  readOnly?: boolean;
  /** `text` — код с подсветкой, `tree` — дерево значений. */
  mode?: 'text' | 'tree';
  label?: string;
}) {
  const host = useRef<HTMLDivElement>(null);
  // Ссылка на редактор нужна между эффектами. Тип берётся импортом типа:
  // он не тянет за собой сам пакет в бандл страницы.
  const editor = useRef<Editor | null>(null);
  // Обработчик держим в ссылке: пересоздавать редактор на каждый рендер
  // родителя означало бы терять курсор при вводе.
  const emit = useRef(onChange);
  emit.current = onChange;
  // Текущий текст внутри редактора, чтобы не записывать в него то, что он
  // сам только что прислал: обратная запись сбрасывает позицию курсора.
  const current = useRef(value);

  useEffect(() => {
    // Флаг в объекте, а не в переменной: между `await` и проверкой эффект
    // может быть уже свёрнут, и анализатору потока это видно только так.
    const life = { disposed: false };
    const node = host.current;
    if (!node) return;

    void (async () => {
      const { createJSONEditor } = await import('vanilla-jsoneditor');
      if (life.disposed) return;

      editor.current = createJSONEditor({
        target: node,
        props: {
          content: { text: current.current },
          mode: mode === 'tree' ? 'tree' : 'text',
          readOnly,
          mainMenuBar: false,
          navigationBar: mode === 'tree',
          statusBar: false,
          onChange: (content: { text?: string; json?: unknown }) => {
            const text =
              typeof content.text === 'string'
                ? content.text
                : JSON.stringify(content.json ?? null, null, 2);

            current.current = text;
            emit.current?.(text);
          },
        },
      });
    })();

    return () => {
      life.disposed = true;
      // Снос редактора асинхронный, но ждать его тут нечем: узел всё равно
      // уходит вместе с компонентом.
      void editor.current?.destroy();
      editor.current = null;
    };
    // Режим меняет саму раскладку редактора, поэтому он пересоздаётся.
  }, [mode, readOnly]);

  // Текст, пришедший снаружи: открыт другой документ, применено
  // форматирование или прилетела правка от соавтора.
  useEffect(() => {
    if (value === current.current) return;
    current.current = value;
    editor.current?.update({ text: value });
  }, [value]);

  return (
    <div
      ref={host}
      className="dh-json-editor h-full min-h-[420px]"
      role="group"
      aria-label={label}
    />
  );
}
